import { useEffect, useMemo, useRef, useState } from 'react';
import { useClickAway, useMedia } from 'react-use';
import { BreakpointQueryLarge } from '@hungryroot/tokens';

type UseTooltipManagerProps = {
  showOnCreate: boolean;
  scrollDismiss: boolean;
  dismissDelay?: number;
  dismissClickTarget?: string;
  onShown?: () => void;
  trigger?: 'manual' | 'click' | 'hover';
  hideOnClick?: boolean;
};

export const useTooltipManager = ({
  showOnCreate,
  scrollDismiss,
  dismissDelay,
  dismissClickTarget,
  onShown,
  trigger: triggerProp,
  hideOnClick,
}: UseTooltipManagerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLButtonElement>(null);

  const isDesktop = useMedia(BreakpointQueryLarge);

  const trigger = useMemo(() => {
    if (isDesktop) {
      return triggerProp;
    }
    return triggerProp === undefined ? 'click' : triggerProp;
  }, [isDesktop, triggerProp]);

  // Handle clicks outside the tooltip
  useClickAway(containerRef, () => {
    if (hideOnClick) {
      setIsOpen(false);
    }
  });

  /**
   * Handler to intercept the library's setOpen method
   * This is called when the tooltip trigger is focused or hovered over
   * In some cases, we do not want that functionality, this handler allows use to reject those unwanted updates
   * */
  const handleLibSetOpen = (val: boolean) => {
    if (trigger !== 'manual' && trigger !== 'click') {
      setIsOpen(val);
    }
  };

  // handle "click" triggers - adds event listeners to the container element to toggle the tooltip
  useEffect(() => {
    const element = containerRef.current;
    const onClick = () => {
      setIsOpen((prev) => !prev);
    };

    if (trigger === 'click') {
      if (element) {
        element.addEventListener('click', onClick);
      }
    }

    return () => {
      if (element) {
        element.removeEventListener('click', onClick);
      }
    };
  }, [trigger]);

  // open the tooltip on creation if the showOnCreate prop is true
  useEffect(() => {
    if (showOnCreate) {
      setIsOpen(true);
    }
  }, [showOnCreate]);

  // handle dismissing the tooltip on scroll
  useEffect(() => {
    const onScroll = () => {
      setIsOpen(false);
    };

    if (scrollDismiss && isOpen) {
      window.addEventListener('scroll', onScroll);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isOpen, scrollDismiss]);

  // handle dismissing the tooltip after a delay
  useEffect(() => {
    if (dismissDelay !== undefined && isOpen) {
      const timer = setTimeout(() => {
        setIsOpen(false);
      }, dismissDelay);
      return () => clearTimeout(timer);
    }
  }, [dismissDelay, isOpen]);

  // handle dismissing the tooltip when the user clicks outside of it
  useEffect(() => {
    const onClick = () => {
      setIsOpen(false);
    };

    if (dismissClickTarget) {
      const target = document.querySelector(dismissClickTarget);
      target?.addEventListener('click', onClick);

      return () => {
        if (target) {
          target.removeEventListener('click', onClick);
        }
      };
    }
  }, [dismissClickTarget]);

  useEffect(() => {
    if (onShown) {
      onShown();
    }
  }, [onShown]);

  return {
    isOpen,
    setIsOpen: handleLibSetOpen,
    containerRef,
  };
};
